import { useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface CombinedWorkflowFilterProps {
  selectedValue: string;
  workflowNames: string[];
  workflowIds: string[];
  onSelectionChange: (value: string) => void;
}

export const CombinedWorkflowFilter = ({
  selectedValue,
  workflowNames,
  workflowIds,
  onSelectionChange,
}: CombinedWorkflowFilterProps) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (value: string) => {
    onSelectionChange(value === selectedValue ? '' : value);
    setOpen(false);
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange('');
  };

  const getDisplayText = () => {
    if (!selectedValue) return 'Search workflows...';
    return selectedValue.length > 25 ? `${selectedValue.substring(0, 25)}...` : selectedValue;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-between min-w-[300px]">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            <span className="truncate">{getDisplayText()}</span>
          </div>
          <div className="flex items-center gap-1">
            {selectedValue && (
              <X className="h-4 w-4 opacity-50 hover:opacity-100" onClick={clearSelection} />
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by workflow name or run ID..." />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup heading="Workflow Names">
              {workflowNames.map((name) => {
                const isSelected = selectedValue === name;
                return (
                  <CommandItem
                    key={`name-${name}`}
                    onSelect={() => handleSelect(name)}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate">{name}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Run IDs">
              {workflowIds.map((id) => {
                const isSelected = selectedValue === id;
                return (
                  <CommandItem
                    key={`id-${id}`}
                    onSelect={() => handleSelect(id)}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate font-mono text-sm">{id}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
